import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
  CognitoUserAttribute,
  ICognitoUserData,
  ICognitoUserPoolData,
} from 'amazon-cognito-identity-js';


export interface CognitoUserAttributes {
  email: string;
  email_verified: string;
  sub: string;
  'custom:dni'?: string;
  name?: string;
  [key: string]: string | undefined;
}

export interface CognitoSessionData {
  idToken: string;
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  payload: any;
}

export interface NewPasswordRequiredDetails {
  userAttributes: any;
  requiredAttributes: any;
}

const poolData: ICognitoUserPoolData = {
  UserPoolId: process.env.REACT_APP_USER_POOL_ID || '',
  ClientId: process.env.REACT_APP_USER_POOL_CLIENT_ID || '',
};

const userPool = new CognitoUserPool(poolData);

class CognitoService {
  signIn(username: string, password: string): Promise<CognitoUserSession> {
    return new Promise((resolve, reject) => {
      const authenticationDetails = new AuthenticationDetails({
        Username: username,
        Password: password,
      });

      const userData: ICognitoUserData = {
        Username: username,
        Pool: userPool,
      };

      const cognitoUser = new CognitoUser(userData);

      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: (session: CognitoUserSession) => {
          resolve(session);
        },
        onFailure: (err: any) => {
          reject(this.handleCognitoError(err));
        },
        newPasswordRequired: (userAttributes: any, requiredAttributes: any) => {
          reject({
            code: 'NewPasswordRequired',
            message: 'Debes cambiar tu contraseña',
            userAttributes,
            requiredAttributes,
          });
        },
      });
    });
  }

  signOut(): void {
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) {
      cognitoUser.signOut();
    }
  }

  getSession(): Promise<CognitoUserSession> {
    return new Promise((resolve, reject) => {
      const cognitoUser = userPool.getCurrentUser();
      if (!cognitoUser) {
        reject(new Error('No hay usuario logueado'));
        return;
      }

      cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err) {
          reject(err);
          return;
        }
        if (!session) {
          reject(new Error('No se pudo obtener la sesión'));
          return;
        }

        if (!session.isValid()) {
          reject(new Error('Sesión inválida'));
          return;
        }

        resolve(session);
      });
    });
  }

  getUserAttributes(): Promise<CognitoUserAttributes> {
    return new Promise((resolve, reject) => {
      const cognitoUser = userPool.getCurrentUser();
      if (!cognitoUser) {
        reject(new Error('No hay usuario logueado'));
        return;
      }
      // @ts-ignore
      cognitoUser.getSession((err: Error, session: CognitoUserSession) => {
        if (err) {
          reject(err);
          return;
        }

        cognitoUser.getUserAttributes((err: Error | undefined, attributes: CognitoUserAttribute[] | undefined) => {
          if (err) {
            reject(err);
            return;
          }

          const userAttributes: CognitoUserAttributes = {} as CognitoUserAttributes;
          attributes?.forEach((attr) => {
            userAttributes[attr.getName()] = attr.getValue();
          });

          resolve(userAttributes);
        });
      });
    });
  }

  changePassword(oldPassword: string, newPassword: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const cognitoUser = userPool.getCurrentUser();
      if (!cognitoUser) {
        reject(new Error('No hay usuario logueado'));
        return;
      }
        // @ts-ignore
      cognitoUser.getSession((err: Error, session: CognitoUserSession) => {
        if (err) {
          reject(err);
          return;
        }

        cognitoUser.changePassword(oldPassword, newPassword, (err: Error | undefined, result: string | undefined) => {
          if (err) {
            reject(this.handleCognitoError(err));
            return;
          }
          resolve(result || 'Contraseña cambiada exitosamente');
        });
      });
    });
  }

  forgotPassword(email: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const userData: ICognitoUserData = {
        Username: email,
        Pool: userPool,
      };

      const cognitoUser = new CognitoUser(userData);

      cognitoUser.forgotPassword({
        onSuccess: () => {
          resolve();
        },
        onFailure: (err: any) => {
          reject(this.handleCognitoError(err));
        },
      });
    });
  }

  confirmPassword(email: string, code: string, newPassword: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const userData: ICognitoUserData = {
        Username: email,
        Pool: userPool,
      };

      const cognitoUser = new CognitoUser(userData);

      cognitoUser.confirmPassword(code, newPassword, {
        onSuccess: () => {
          resolve();
        },
        onFailure: (err: any) => {
          reject(this.handleCognitoError(err));
        },
      });
    });
  }

  completeNewPassword(newPassword: string, userAttributes: any): Promise<CognitoUserSession> {
    return new Promise((resolve, reject) => {
      const cognitoUser = userPool.getCurrentUser();
      if (!cognitoUser) {
        reject(new Error('No hay usuario logueado'));
        return;
      }

      cognitoUser.completeNewPasswordChallenge(newPassword, userAttributes, {
        onSuccess: (session: CognitoUserSession) => {
          resolve(session);
        },
        onFailure: (err: any) => {
          reject(this.handleCognitoError(err));
        },
      });
    });
  }

  async getAccessToken(): Promise<string | null> {
    try {
      const session = await this.getSession();
      return session.getAccessToken().getJwtToken();
    } catch (error) {
      console.error('Error getting access token:', error);
      return null;
    }
  }
  async getIdToken(): Promise<string | null> {
    try {
      const session = await this.getSession();
      return session.getIdToken().getJwtToken();
    } catch (error) {
      console.error('Error obteniendo ID Token:', error);
      return null;
    }
  }

  saveSessionToStorage(session: CognitoUserSession): void {
    try {
      const sessionData: CognitoSessionData = {
        idToken: session.getIdToken().getJwtToken(),
        accessToken: session.getAccessToken().getJwtToken(),
        refreshToken: session.getRefreshToken().getToken(),
        expiresIn: session.getAccessToken().getExpiration(),
        payload: session.getIdToken().payload,
      };
      localStorage.setItem('cognitoSession', JSON.stringify(sessionData));
    } catch (error) {
      console.error('Error saving session:', error);
    }
  }

  loadSessionFromStorage(): CognitoSessionData | null {
    try {
      const sessionData = localStorage.getItem('cognitoSession');
      return sessionData ? JSON.parse(sessionData) : null;
    } catch (error) {
      console.error('Error loading session:', error);
      return null;
    }
  }

  private handleCognitoError(error: any): Error {
    console.error('Cognito error:', error);

    let message = 'Error de autenticación';

    if (error.code === 'UserNotFoundException' || error.message?.includes('User does not exist')) {
      message = 'Usuario no encontrado';
    } else if (error.code === 'NotAuthorizedException' || error.message?.includes('Incorrect username or password')) {
      message = 'Usuario o contraseña incorrectos';
    } else if (error.code === 'UserNotConfirmedException') {
      message = 'Usuario no confirmado';
    } else if (error.code === 'PasswordResetRequiredException') {
      message = 'Requiere cambio de contraseña';
    } else if (error.code === 'InvalidPasswordException' || error.message?.includes('Password did not conform')) {
      message = 'La contraseña debe tener al menos 6 caracteres';
    } else if (error.code === 'ExpiredCodeException') {
      message = 'El código ha expirado';
    } else if (error.code === 'CodeMismatchException') {
      message = 'Código incorrecto';
    } else if (error.code === 'LimitExceededException') {
      message = 'Demasiados intentos. Intenta más tarde';
    } else if (error.code === 'NewPasswordRequired') {
      message = error.message;
    }

    return new Error(message);
  }
}

export const cognitoService = new CognitoService();
export default cognitoService;